import { DOCUMENT, NgClass } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ConfigService } from '@config';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import { LanguageService, InConfiguration, AuthService, UserRole, EnterpriseSelectionService, NotificationDataService, NotificationSignalRService, UserNotification, NotificationService } from '@core';
import { UserService } from '@core/service/user.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { MatMenuModule } from '@angular/material/menu';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BrandingService } from '@core/service/branding.service';
import { PartnerBranding } from '@core/models/partner.model';

interface Notifications {
  id: string;
  message: string;
  time: string;
  icon: string;
  color: string;
  status: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    NgClass,
    MatButtonModule,
    MatBadgeModule,
    FeatherIconsComponent,
    MatMenuModule,
    MatTooltipModule,
    NgScrollbar,
    TranslateModule
  ],
})
export class HeaderComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit
{
  public config!: InConfiguration;
  userImg?: string;
  userFullName?: string;
  userEmail?: string;
  userRoleKey = '';
  homePage?: string;
  isNavbarCollapsed = true;
  flagvalue: string | string[] | undefined;
  countryName: string | string[] = [];
  langStoreValue?: string;
  defaultFlag?: string;
  isOpenSidebar?: boolean;
  docElement?: HTMLElement;
  isFullScreen = false;
  isDarkTheme: boolean = false;
  unreadCount = 0;
  branding: PartnerBranding | null = null;
  private pollingSubscription?: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private configService: ConfigService,
    private authService: AuthService,
    private userService: UserService,
    private notificationDataService: NotificationDataService,
    private notificationSignalRService: NotificationSignalRService,
    private notificationService: NotificationService,
    private enterpriseSelectionService: EnterpriseSelectionService,
    @Inject(Router) private router: Router,
    public languageService: LanguageService,
    public brandingService: BrandingService
  ) {
    super();
    if (localStorage.getItem('theme')) {
      if (localStorage.getItem('theme') === 'dark') {
        this.isDarkTheme = true;
      } else if (localStorage.getItem('theme') === 'light') {
        this.isDarkTheme = false;
      }
    }

    this.toggleTheme();

    // Restore sidebar collapsed state from localStorage
    const isMenuCollapsed = localStorage.getItem('collapsed_menu') === 'true';
    if (isMenuCollapsed) {
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
    }
  }
  listLang = [
    { text: 'Português', flag: 'assets/images/flags/brazil.jpg', lang: 'pt' },
    { text: 'Inglês', flag: 'assets/images/flags/us.jpg', lang: 'en' },
    { text: 'Espanhol', flag: 'assets/images/flags/spain.jpg', lang: 'es' }
  ];
  notifications: Notifications[] = [];
  ngOnInit() {
    this.config = this.configService.configData;
    const userRole = this.authService.currentUserValue.role;
    this.userImg = this.authService.currentUserValue.img ? this.authService.currentUserValue.img : 'assets/images/user/avatar.png';
    this.userFullName = this.authService.currentUserValue.firstName + " " + this.authService.currentUserValue.lastName;
    this.userEmail = this.authService.currentUserValue.email;
    this.userRoleKey = this.getRoleTranslationKey(userRole);
    this.docElement = document.documentElement;

    // Subscribe to branding changes
    this.subs.sink = this.brandingService.branding$.subscribe((b) => (this.branding = b));

    // Subscribe to user changes for live avatar/name/role updates
    this.subs.sink = this.authService.currentUser.subscribe((user) => {
      if (user && user.img) {
        this.userImg = user.img;
      }
      if (user && user.firstName) {
        this.userFullName = user.firstName + " " + user.lastName;
      }
      if (user && user.role !== undefined) {
        this.userRoleKey = this.getRoleTranslationKey(user.role);
      }
    });

    // Fetch profile from API to load avatar URL from database
    this.subs.sink = this.userService.getMyProfile().subscribe({
      next: (profile) => {
        if (profile?.avatarUrl) {
          this.authService.updateCurrentUser({ img: profile.avatarUrl });
        }
        if (profile?.firstName) {
          this.authService.updateCurrentUser({
            firstName: profile.firstName,
            lastName: profile.lastName,
          });
        }
        if (profile?.email) {
          this.userEmail = profile.email;
        }
      },
      error: () => { /* silently ignore if profile fetch fails */ }
    });

    if (userRole === UserRole.SuperAdmin) {
      this.homePage = 'master/dashboard/main';
    } else if (userRole === UserRole.PartnerAdmin || userRole === UserRole.Admin) {
      this.homePage = 'admin/dashboard/main';
    } else if (userRole === UserRole.CompanyAdmin) {
      this.homePage = 'company/enterprises';
    } else {
      this.homePage = 'enterprise/dashboard';
    }

    this.langStoreValue = localStorage.getItem('lang') as string;
    const val = this.listLang.filter((x) => x.lang === this.langStoreValue);
    this.countryName = val.map((element) => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.defaultFlag = 'assets/images/flags/brazil.jpg';
        this.languageService.setLanguage('pt');
      }
    } else {
      this.flagvalue = val.map((element) => element.flag);
    }

    // Subscribe to enterprise changes and load notifications
    this.subs.sink = this.enterpriseSelectionService.selectedEnterprise$.subscribe(
      (enterpriseId) => {
        if (enterpriseId) {
          this.loadNotifications(enterpriseId);
          this.startNotificationPolling(enterpriseId);
          this.notificationSignalRService.startConnection(enterpriseId);
        }
      }
    );

    // Subscribe to unread count changes
    this.subs.sink = this.notificationDataService.unreadCount$.subscribe(
      (count) => (this.unreadCount = count)
    );

    // Real-time: refresh notifications when SignalR receives a new notification
    this.subs.sink = this.notificationSignalRService.notificationReceived$.subscribe(
      (notification) => {
        this.notifications.unshift({
          id: '',
          message: notification.title,
          time: this.getRelativeTime(new Date(notification.createdAt)),
          icon: notification.icon,
          color: notification.color,
          status: 'msg-unread',
        });
        // Keep only the latest 7 in the dropdown
        if (this.notifications.length > 7) {
          this.notifications = this.notifications.slice(0, 7);
        }
      }
    );

    // Real-time: refresh unread count when SignalR signals an update
    this.subs.sink = this.notificationSignalRService.unreadCountUpdated$.subscribe(
      () => {
        const enterpriseId = this.getSelectedEnterpriseId();
        if (enterpriseId) {
          this.subs.sink = this.notificationDataService
            .getUnreadCount(enterpriseId)
            .subscribe();
        }
      }
    );
  }

  toggleTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    localStorage.setItem('theme', this.isDarkTheme ? 'dark' : 'light');
    this.applyTheme();
  }

  applyTheme() {
    if (this.isDarkTheme) {
      this.setLightTheme();
    } else {
      this.setDarkTheme();
    }
  }

  setDarkTheme() {
    this.renderer.removeClass(this.document.body, 'light');
    this.renderer.removeClass(this.document.body, 'submenu-closed');
    this.renderer.removeClass(this.document.body, 'menu_light');
    this.renderer.removeClass(this.document.body, 'logo-white');
    if (localStorage.getItem('choose_skin')) {
      this.renderer.removeClass(
        this.document.body,
        localStorage.getItem('choose_skin') as string
      );
    } else if (this.config && this.config.layout && this.config.layout.theme_color) {
      this.renderer.removeClass(
        this.document.body,
        'theme-' + this.config.layout.theme_color
      );
    }
    this.renderer.addClass(this.document.body, 'dark');
    this.renderer.addClass(this.document.body, 'submenu-closed');
    this.renderer.addClass(this.document.body, 'menu_dark');
    this.renderer.addClass(this.document.body, 'logo-black');
    this.renderer.addClass(this.document.body, 'theme-black');
    const theme = 'dark';
    const menuOption = 'menu_dark';
    localStorage.setItem('choose_logoheader', 'logo-black');
    localStorage.setItem('choose_skin', 'theme-black');
    localStorage.setItem('theme', theme);
    localStorage.setItem('menuOption', menuOption);
  }

  setLightTheme() {
    this.renderer.removeClass(this.document.body, 'dark');
    this.renderer.removeClass(this.document.body, 'submenu-closed');
    this.renderer.removeClass(this.document.body, 'menu_dark');
    this.renderer.removeClass(this.document.body, 'logo-black');
    if (localStorage.getItem('choose_skin')) {
      this.renderer.removeClass(
        this.document.body,
        localStorage.getItem('choose_skin') as string
      );
    } else if (this.config && this.config.layout && this.config.layout.theme_color) {
      this.renderer.removeClass(
        this.document.body,
        'theme-' + this.config.layout.theme_color
      );
    }

    this.renderer.addClass(this.document.body, 'light');
    this.renderer.addClass(this.document.body, 'submenu-closed');
    this.renderer.addClass(this.document.body, 'menu_light');
    this.renderer.addClass(this.document.body, 'logo-white');
    this.renderer.addClass(this.document.body, 'theme-white');
    const theme = 'light';
    const menuOption = 'menu_light';
    localStorage.setItem('choose_logoheader', 'logo-white');
    localStorage.setItem('choose_skin', 'theme-white');
    localStorage.setItem('theme', theme);
    localStorage.setItem('menuOption', menuOption);
  }

  callFullscreen() {
    if (!this.isFullScreen) {
      if (this.docElement?.requestFullscreen != null) {
        this.docElement?.requestFullscreen();
      }
    } else {
      document.exitFullscreen();
    }
    this.isFullScreen = !this.isFullScreen;
  }
  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.langStoreValue = lang;
    this.languageService.setLanguage(lang);

    // Force immediate update of all datepickers
    setTimeout(() => {
      this.forceUpdateAllDatepickers();
    }, 150);
  }

  private forceUpdateAllDatepickers() {
    // Strategy 1: Force change detection on all datepicker elements
    const datepickerElements = this.document.querySelectorAll('mat-datepicker-input, mat-datepicker-toggle, .mat-datepicker-input');
    datepickerElements.forEach((element: any) => {
      if (element) {
        // Trigger events to force update
        element.dispatchEvent(new Event('blur', { bubbles: true }));
        setTimeout(() => {
          element.dispatchEvent(new Event('focus', { bubbles: true }));
        }, 10);
      }
    });

    // Strategy 2: Force update of Angular Material components
    const matFormFields = this.document.querySelectorAll('mat-form-field');
    matFormFields.forEach((field: any) => {
      if (field && field._elementRef) {
        field._elementRef.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    console.log('Forced update of all datepickers after language change');
  }
  mobileMenuSidebarOpen(event: Event, className: string) {
    const hasClass = (event.target as HTMLInputElement).classList.contains(
      className
    );
    if (hasClass) {
      this.renderer.removeClass(this.document.body, className);
    } else {
      this.renderer.addClass(this.document.body, className);
    }
  }
  callSidemenuCollapse() {
    const hasClass = this.document.body.classList.contains('side-closed');
    if (hasClass) {
      this.renderer.removeClass(this.document.body, 'side-closed');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
      localStorage.setItem('collapsed_menu', 'false');
    } else {
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
      localStorage.setItem('collapsed_menu', 'true');
    }
  }
  goToProfile() {
    this.router.navigate(['/profile']);
  }

  goToAbout() {
    this.router.navigate(['/about']);
  }

  goToDocs() {
    window.open(environment.docsUrl, '_blank', 'noopener,noreferrer');
  }

  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (!res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }

  private loadNotifications(enterpriseId: string): void {
    this.subs.sink = this.notificationDataService
      .getNotifications(enterpriseId, 1, 7)
      .subscribe({
        next: (response) => {
          this.notifications = response.data.map((n) => ({
            id: n.id,
            message: n.title,
            time: this.getRelativeTime(n.createdAt),
            icon: n.icon,
            color: n.color,
            status: n.isRead ? 'msg-read' : 'msg-unread',
          }));
        },
        error: () => {
          this.notifications = [];
        },
      });
  }

  private startNotificationPolling(enterpriseId: string): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = this.notificationDataService
      .startPolling(enterpriseId, 30000)
      .subscribe();
  }

  markAllAsRead(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const enterpriseId = this.getSelectedEnterpriseId();
    if (!enterpriseId) return;

    this.subs.sink = this.notificationDataService
      .markAllAsRead(enterpriseId)
      .subscribe(() => {
        this.notifications = this.notifications.map((n) => ({
          ...n,
          status: 'msg-read',
        }));
        this.notificationService.showSuccess('Todas as notificações foram marcadas como lidas');
      });
  }

  markAsRead(notification: Notifications, event: Event): void {
    event.stopPropagation();
    if (notification.status === 'msg-read') return;

    this.subs.sink = this.notificationDataService
      .markAsRead(notification.id)
      .subscribe(() => {
        notification.status = 'msg-read';
        this.notificationDataService.decrementUnreadCount();
      });
  }

  dismissNotification(notification: Notifications, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.subs.sink = this.notificationDataService
      .deleteNotification(notification.id)
      .subscribe(() => {
        this.notifications = this.notifications.filter(
          (n) => n.id !== notification.id
        );
        if (notification.status === 'msg-unread') {
          this.notificationDataService.decrementUnreadCount();
        }
      });
  }

  goToNotifications(): void {
    this.router.navigate(['/enterprise/notifications']);
  }

  private getSelectedEnterpriseId(): string {
    // Read from localStorage or service - EnterpriseSelectionService uses BehaviorSubject
    return localStorage.getItem('selectedEnterpriseId') || '';
  }

  private getRelativeTime(date: Date): string {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `${diffMin} min atrás`;

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} hora${diffHours > 1 ? 's' : ''} atrás`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays} dia${diffDays > 1 ? 's' : ''} atrás`;

    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} mês${diffMonths > 1 ? 'es' : ''} atrás`;
  }

  override ngOnDestroy(): void {
    this.pollingSubscription?.unsubscribe();
    this.notificationSignalRService.stopConnection();
    super.ngOnDestroy();
  }

  private getRoleTranslationKey(role: UserRole): string {
    const keys: Record<number, string> = {
      [UserRole.SuperAdmin]: 'MODELS.USER.ROLE.SUPER-ADMIN',
      [UserRole.PartnerAdmin]: 'MODELS.USER.ROLE.PARTNER-ADMIN',
      [UserRole.Admin]: 'MODELS.USER.ROLE.ADMIN',
      [UserRole.CompanyAdmin]: 'MODELS.USER.ROLE.COMPANY-ADMIN',
      [UserRole.EnterpriseAdmin]: 'MODELS.USER.ROLE.ENTERPRISE-ADMIN',
      [UserRole.EnterpriseAnalyst]: 'MODELS.USER.ROLE.ENTERPRISE-ANALYST',
    };
    return keys[role] || 'MODELS.USER.ROLE.ADMIN';
  }
}
